import * as React from "react";
import {useCallback, useMemo} from "react";
import {Case, formatItem, OccupancyType, ServiceOccupancyContent} from "./types";
import {BoxAccess, MessageView, useBoxes, useOccupancies, usePlatform} from "@variocube/cube-ui";
import {Box, LogLevel} from "@variocube/app-api";
import {useLocalization} from "../i18n";

interface PickupFlowProps {
    pickupCase: Case;
    onDone: () => void;
}

export function PickupFlow({pickupCase, onDone}: PickupFlowProps) {
    const {t} = useLocalization();
    const occupancies = useOccupancies();
    const boxes = useBoxes();

    const occupancy = useMemo(() => occupancies.find(occupancy => occupancy.uuid == pickupCase.reservationUuid), [occupancies, pickupCase]);
    const box = useMemo(() => boxes.find(box => box.number == occupancy?.boxNumber), [boxes, occupancy]);

    if (box) {
        return (
            <PickupBoxAccess box={box} pickupCase={pickupCase} onDone={onDone} />
        );
    }

    return (
        <MessageView message={t("Loading")}/>
    );
}

interface PickupBoxAccessProps {
    box: Box;
    pickupCase: Case;
    onDone: () => void;
}

function PickupBoxAccess({box, pickupCase, onDone}: PickupBoxAccessProps) {
    const platform = usePlatform();
    const {t} = useLocalization();

    const handleConfirm = useCallback(async () => {
        const occupancyUuid = pickupCase.reservationUuid;
        if (occupancyUuid) {
            if (pickupCase.type == OccupancyType.ServiceBox) {
                await platform.log(LogLevel.Audit, "Pickup from ServiceBox", {pickupCase});
            }
            else if (pickupCase.replacement && pickupCase.type == OccupancyType.Outbound) {
                // replacement: change the serviceCase
                const replacementCase = {
                    ... pickupCase,
                    pickupCode: pickupCase.storageCode,
                    storageCode: pickupCase.pickupCode,
                    occupiedFrom: new Date().toISOString(),
                    occupiedUntil: undefined,
                    type: OccupancyType.Inbound,
                    description: pickupCase.replacementDescription || '',
                    item: pickupCase.replacementItem,
                };
                const content: ServiceOccupancyContent = {
                    occupancy: replacementCase
                };
                await platform.updateOccupancy(occupancyUuid, content);
                await platform.log(LogLevel.Audit, "Pickup and Replacement", {pickupCase});
            }
            else {
                // Any other case just end the occupancy
                await platform.endOccupancy(occupancyUuid);
                await platform.log(LogLevel.Audit, "Pickup", {pickupCase});
            }
        }
        onDone();
    }, [platform, pickupCase, onDone]);

    const itemName = formatItem(t("StoreItemUnknownItem"), pickupCase.description, pickupCase.item);
    const replacement = formatItem(t("StoreItemUnknownItem"), pickupCase.replacementDescription, pickupCase.replacementItem);
    const pickupInfo = pickupCase.replacement ?
        t("PickupBoxOpenInfoReplacement", {item: itemName, replacement: replacement, boxNumber: box.number}) :
        t("PickupBoxOpenInfo", {item: itemName, boxNumber: box.number});
    const pickupConfirm = pickupCase.replacement ?
        t("PickupConfirmInfoReplacement", {item: itemName, replacement: replacement, boxNumber: box.number}) :
        t("PickupConfirmInfo", {item: itemName, boxNumber: box.number});

    return (
        <BoxAccess
            boxes={box}
            platform={platform}
            onCancel={onDone}
            onConfirm={handleConfirm}
            boxOpenTitle={t("PickupBoxOpenTitle", {item: itemName})}
            boxOpenMessage={pickupInfo}
            confirmTitle={t("PickupConfirmTitle", {item: itemName})}
            confirmMessage={pickupConfirm}
            strings={t}
        />
    );
}
